import { apiClient, apiDelete, apiGet, apiPatch, apiPost } from './client.js';
import type { PaginatedMeta } from './tickets.js';

export interface InventoryListParams {
  search?: string;
  category?: string;
  subcategoryId?: string;
  categoryId?: string;
  compatibleModelId?: string;
  compatibleMakeId?: string;
  condition?: string;
  supplierId?: string;
  isBundle?: boolean;
  hasVariants?: boolean;
  lowStockOnly?: boolean;
  serialized?: boolean;
  includeArchived?: boolean;
  sellableOnly?: boolean;
  status?: 'active' | 'archived' | 'discontinued' | 'all';
  idsOnly?: boolean;
  sort?: 'name' | 'qty' | 'lastReceived' | 'category';
  page?: number;
  pageSize?: number;
}

export interface CompatibleMakeRef {
  makeId: string;
  makeName: string;
}

export interface CompatibleModelRef {
  modelId: string;
  modelName: string;
  makeId?: string;
  makeName?: string;
  autoSuggested?: boolean;
}

export interface InventoryItemListRow {
  id: string;
  name: string;
  category: string;
  barcodeInternal?: string | null;
  sku?: string | null;
  qtyOnHand: string;
  qtyReserved?: number;
  qtyAvailable?: number;
  binLocation?: string | null;
  retailPrice: string;
  lowStock: boolean;
  reorderPoint?: number | null;
  lastReceivedAt?: string | null;
  supplierCount?: number;
  condition?: string | null;
  compatibleModels?: CompatibleModelRef[];
  compatibleModelIds?: string[];
  compatibleMakes?: CompatibleMakeRef[];
  categoryPath?: string | null;
  preferredSupplier?: { id: string; name: string | null } | null;
  isBundle?: boolean;
  hasVariants?: boolean;
  archivedAt?: string | null;
  archiveReason?: string | null;
  discontinuedAt?: string | null;
  active?: boolean;
}

export type InventoryItemReferences = {
  total: number;
  byTable: Record<string, number>;
  qtyOnHand: number;
  stockByStore: Array<{ storeId: string; qtyOnHand: number }>;
  hasOpenPO: boolean;
  hasReservations: boolean;
  isBundleComponent: boolean;
  hasVariants: boolean;
  canHardDelete: boolean;
  archiveBlockers: Array<{
    kind: string;
    label: string;
    id?: string;
    href?: string;
  }>;
  openPurchaseOrders: Array<{ id: string; poNumber: string; status: string }>;
  openReservations: Array<{ id: string; ticketId: string; ticketNumber: string | null }>;
  bundleParents: Array<{ id: string; name: string }>;
};

export type BulkInventoryResult = {
  dryRun: boolean;
  succeeded: string[];
  skipped: Array<{
    id: string;
    name?: string;
    reason: string;
    blockers?: unknown;
    qtyOnHand?: number;
    byTable?: Record<string, number>;
  }>;
};

export interface InventorySubcategoryRow {
  id: string;
  category: string;
  name: string;
  slug: string;
}

export interface InventoryItemDetail {
  item: Record<string, unknown>;
  suppliers: Array<Record<string, unknown> & { supplierName?: string }>;
  supplierSkus?: Array<Record<string, unknown> & { supplierName?: string }>;
  stock: Record<string, unknown> | null;
  adjustments: Array<Record<string, unknown>>;
  history?: Array<Record<string, unknown>>;
  fifoLayers: Array<Record<string, unknown> & { supplierName?: string | null }>;
  serials?: Array<Record<string, unknown>>;
  bundleComponents?: Array<Record<string, unknown>>;
  variants?: Array<Record<string, unknown>>;
  reservations?: Array<Record<string, unknown>>;
}

export function listInventoryItems(params?: InventoryListParams) {
  return apiGet<InventoryItemListRow[], PaginatedMeta>('/api/v1/inventory/items', { params });
}

export function listInventoryItemIds(
  params?: Omit<InventoryListParams, 'page' | 'pageSize' | 'idsOnly'>,
) {
  return apiGet<string[], PaginatedMeta>('/api/v1/inventory/items', {
    params: { ...params, idsOnly: true, page: 1, pageSize: 100 },
  });
}

export function getInventoryItemReferences(id: string) {
  return apiGet<InventoryItemReferences>(`/api/v1/inventory/items/${id}/references`).then(
    (r) => r.data,
  );
}

export function archiveInventoryItem(
  id: string,
  body?: { reason?: string | null; acknowledgeStock?: boolean; writeOffStock?: boolean },
) {
  return apiPost(`/api/v1/inventory/items/${id}/archive`, body ?? {}).then((r) => r.data);
}

export function unarchiveInventoryItem(id: string) {
  return apiPost(`/api/v1/inventory/items/${id}/unarchive`, {}).then((r) => r.data);
}

export function discontinueInventoryItem(id: string) {
  return apiPost(`/api/v1/inventory/items/${id}/discontinue`, {}).then((r) => r.data);
}

export function reactivateInventoryItem(id: string) {
  return apiPost(`/api/v1/inventory/items/${id}/reactivate`, {}).then((r) => r.data);
}

export function deleteInventoryItem(
  id: string,
  body: { reason: string; confirmName: string; managerOverrideAuthorizedBy: string },
) {
  return apiDelete(`/api/v1/inventory/items/${id}`, { data: body }).then((r) => r.data);
}

export function bulkArchiveInventory(body: {
  itemIds: string[];
  reason?: string | null;
  acknowledgeStock?: boolean;
  writeOffStock?: boolean;
  dryRun?: boolean;
}) {
  return apiPost<BulkInventoryResult>('/api/v1/inventory/bulk/archive', body).then((r) => r.data);
}

export function bulkUnarchiveInventory(body: { itemIds: string[]; dryRun?: boolean }) {
  return apiPost<BulkInventoryResult>('/api/v1/inventory/bulk/unarchive', body).then((r) => r.data);
}

export function bulkDiscontinueInventory(body: { itemIds: string[]; dryRun?: boolean }) {
  return apiPost<BulkInventoryResult>('/api/v1/inventory/bulk/discontinue', body).then((r) => r.data);
}

export function bulkDeleteInventory(body: {
  itemIds: string[];
  reason: string;
  confirmText: 'DELETE';
  managerOverrideAuthorizedBy?: string;
  dryRun?: boolean;
}) {
  return apiPost<BulkInventoryResult>('/api/v1/inventory/bulk/delete', body).then((r) => r.data);
}

/** Cache prefixes to invalidate after archive / discontinue / delete. */
export const INVENTORY_CACHE_PREFIXES = [
  'inventory-items',
  'inventory-item',
  'pos-scan',
  'pos-pinned',
  'pos-recent-items',
  'pos-accessories',
  'pos-misc',
  'inventory-items-compatible',
  'inventory-items-parts',
  'inventory-items-bundle-pick',
  'inventory-items-rma',
  'po-item-search',
  'po-low-stock',
  'palette-inventory',
] as const;

export function getInventorySubcategories() {
  return apiGet<InventorySubcategoryRow[]>('/api/v1/inventory/subcategories').then((r) => r.data);
}

export function getInventoryItemDetail(id: string) {
  return apiGet<InventoryItemDetail>(`/api/v1/inventory/items/${id}`).then((r) => r.data);
}

export function createInventoryItem(body: Record<string, unknown>) {
  return apiPost<Record<string, unknown>, Record<string, unknown>>('/api/v1/inventory/items', body).then(
    (res) => res.data,
  );
}

export function patchInventoryItem(id: string, body: Record<string, unknown>) {
  return apiPatch<Record<string, unknown>, Record<string, unknown>>(
    `/api/v1/inventory/items/${id}`,
    body,
  ).then((res) => res.data);
}

export function postInventoryAdjust(
  itemId: string,
  body: {
    delta: number;
    reasonCode: string;
    reasonNotes?: string;
    adjustmentType?: 'manual' | 'po_receive';
    unitCost?: number;
    supplierVariantId?: string;
  },
) {
  return apiPost<Record<string, unknown>, Record<string, unknown>>(
    `/api/v1/inventory/items/${itemId}/adjust`,
    body,
  ).then((res) => res.data);
}

export function postItemSupplier(
  itemId: string,
  body: {
    supplierId: string;
    supplierSku?: string;
    supplierBarcode?: string;
    costPrice: number;
    leadTimeDays?: number;
    minimumOrderQty?: number;
    preferred?: boolean;
    supplierNotes?: string;
  },
) {
  return apiPost<Record<string, unknown>, Record<string, unknown>>(
    `/api/v1/inventory/items/${itemId}/suppliers`,
    body,
  ).then((res) => res.data);
}

export function patchItemSupplier(
  itemId: string,
  variantId: string,
  body: Record<string, unknown>,
) {
  return apiPatch<Record<string, unknown>, Record<string, unknown>>(
    `/api/v1/inventory/items/${itemId}/suppliers/${variantId}`,
    body,
  ).then((res) => res.data);
}

export function postItemSerial(
  itemId: string,
  body: { serialNumber?: string; imei?: string; conditionGrade?: string; internalNotes?: string },
) {
  return apiPost<Record<string, unknown>, Record<string, unknown>>(
    `/api/v1/inventory/items/${itemId}/serials`,
    body,
  ).then((res) => res.data);
}

export function postBundleComponent(
  itemId: string,
  body: { componentItemId: string; quantity?: number; sortOrder?: number },
) {
  return apiPost<Record<string, unknown>>(
    `/api/v1/inventory/items/${itemId}/bundle-components`,
    body,
  ).then((r) => r.data);
}

export function deleteBundleComponent(itemId: string, componentItemId: string) {
  return apiDelete(`/api/v1/inventory/items/${itemId}/bundle-components/${componentItemId}`);
}

export function postItemVariant(
  itemId: string,
  body: {
    name: string;
    attributes?: Record<string, string>;
    retailPrice?: number;
    skuSuffix?: string;
  },
) {
  return apiPost<Record<string, unknown>>(`/api/v1/inventory/items/${itemId}/variants`, body).then(
    (r) => r.data,
  );
}

/**
 * Barcode scan responses are `{ type, data }` (not the usual `{ data }` envelope).
 */
export async function scanBarcode(barcode: string): Promise<{
  type: string;
  data: Record<string, unknown>;
}> {
  const res = await apiClient.get<{ type: string; data: Record<string, unknown> }>(
    `/api/v1/inventory/scan/${encodeURIComponent(barcode)}`,
  );
  return res.data;
}

export async function downloadInventoryExport(
  params: InventoryListParams,
  format: 'xlsx' | 'csv',
): Promise<Blob> {
  const token = (await import('@/stores/auth.js')).useAuthStore.getState().token;
  const search = new URLSearchParams();
  search.set('format', format);
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === '' || key === 'page' || key === 'pageSize' || key === 'idsOnly') continue;
    search.set(key, String(value));
  }
  const base = apiClient.defaults.baseURL ?? '';
  const res = await fetch(`${base}/api/v1/inventory/items/export?${search}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Export failed');
  return res.blob();
}

export type InventoryBulkBatch = {
  id: string;
  itemCount: number;
  reason: string | null;
  performedAt: string;
  reversedAt: string | null;
};

export function listInventoryBulkEdits() {
  return apiGet<InventoryBulkBatch[]>('/api/v1/inventory/bulk-edits').then((r) => r.data);
}

export function undoInventoryBulkEdit(id: string) {
  return apiPost<{ id: string; undone: boolean }>(`/api/v1/inventory/bulk-edits/${id}/undo`, {}).then(
    (r) => r.data,
  );
}
